import { addDoc, collection, doc, getDoc, increment, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";

const VISITOR_KEY = "gf_analytics_visitor_id";
const SESSION_KEY = "gf_analytics_session";
const EXCLUDED_KEY = "gf_analytics_excluded";

const BROWSER_EXCLUSION_MIGRATION_KEY = "gf_browser_exclusion_migrated_v2";

export const ANALYTICS_CONFIG = {
  enabled: true,
  requireConsent: false,
  sessionTimeoutMs: 30 * 60 * 1000,
};

let cachedContext = null;
let initializingContext = null;
let lastPageViewKey = "";
let lastPageViewAt = 0;
let analyticsIdentityPromise = null;
let cachedAnalyticsIdentity = null;
let browserExclusionPromise = null;

let cachedBrowserExclusion = null;

const BROWSER_EXCLUSION_CACHE_MS = 60 * 1000;

if (typeof window !== "undefined") {
  window.addEventListener("gofoton:analytics-exclusion-changed", () => {
    cachedBrowserExclusion = null;

    browserExclusionPromise = null;

    cachedContext = null;
  });
}

const getServerAnalyticsIdentity = async () => {
  if (cachedAnalyticsIdentity) return cachedAnalyticsIdentity;

  if (!analyticsIdentityPromise) {
    analyticsIdentityPromise = fetch("https://us-central1-gofoton-55796.cloudfunctions.net/analyticsIdentity")
      .then((response) => {
        if (!response.ok) throw new Error("Identity request failed");
        return response.json();
      })
      .then((data) => {
        cachedAnalyticsIdentity = data;
        return data;
      })
      .catch((error) => {
        console.error("Analytics identity lookup failed:", error);

        return {
          countryCode: "",
          regionCode: "",
          region: "",
          city: "",
        };
      });
  }

  return analyticsIdentityPromise;
};

const isBrowserExcluded = async (browserId) => {
  /*
      Preserve the old local browser
      block until the admin dashboard
      migrates it into Firestore.
    */
  const legacyLocalFallback =
    localStorage.getItem(EXCLUDED_KEY) === "true" && localStorage.getItem(BROWSER_EXCLUSION_MIGRATION_KEY) !== "true";

  if (
    cachedBrowserExclusion?.browserId === browserId &&
    Date.now() - Number(cachedBrowserExclusion.checkedAt || 0) < BROWSER_EXCLUSION_CACHE_MS
  ) {
    return cachedBrowserExclusion.excluded;
  }

  if (!browserExclusionPromise) {
    browserExclusionPromise = getDoc(doc(db, "analyticsBrowserExclusions", browserId))
      .then((snapshot) => {
        const excluded = snapshot.exists() ? snapshot.data()?.active !== false : legacyLocalFallback;

        cachedBrowserExclusion = {
          browserId,
          excluded,

          checkedAt: Date.now(),
        };

        if (snapshot.exists() && excluded) {
          localStorage.setItem(EXCLUDED_KEY, "true");

          localStorage.setItem(BROWSER_EXCLUSION_MIGRATION_KEY, "true");
        } else if (!legacyLocalFallback) {
          localStorage.removeItem(EXCLUDED_KEY);
        }

        return excluded;
      })
      .catch((error) => {
        console.error("Browser analytics exclusion lookup failed:", error);

        cachedBrowserExclusion = {
          browserId,

          excluded: legacyLocalFallback,

          checkedAt: Date.now(),
        };

        return legacyLocalFallback;
      })
      .finally(() => {
        browserExclusionPromise = null;
      });
  }

  return browserExclusionPromise;
};

const makeId = (prefix) => {
  const random = window.crypto?.randomUUID?.() || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${random}`;
};

const getDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isExcludedPath = (path = "") =>
  path === "/admin" ||
  path.startsWith("/admin/") ||
  path === "/login" ||
  path.startsWith("/login/") ||
  path === "/portal" ||
  path.startsWith("/portal/");

const getDeviceCategory = () => {
  const ua = navigator.userAgent || "";
  const width = window.innerWidth;

  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  if (width <= 767) return "mobile";
  if (width <= 1180) return "tablet";
  return "desktop";
};

const getTrafficSource = () => {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source") || "";
  const utmMedium = params.get("utm_medium") || "";
  const utmCampaign = params.get("utm_campaign") || "";

  if (utmSource || utmMedium || utmCampaign) {
    return {
      sourceType: "campaign",
      sourceName: utmSource || "campaign",
      utmSource,
      utmMedium,
      utmCampaign,
      referrer: document.referrer || "",
      referrerDomain: "",
    };
  }

  if (!document.referrer) {
    return {
      sourceType: "direct",
      sourceName: "Direct",
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
      referrer: "",
      referrerDomain: "",
    };
  }

  try {
    const referrerUrl = new URL(document.referrer);

    if (referrerUrl.origin === window.location.origin) {
      return {
        sourceType: "internal",
        sourceName: "Internal Navigation",
        utmSource: "",
        utmMedium: "",
        utmCampaign: "",
        referrer: document.referrer,
        referrerDomain: referrerUrl.hostname,
      };
    }

    const host = referrerUrl.hostname.toLowerCase();

    if (
      host.includes("google.") ||
      host.includes("bing.com") ||
      host.includes("yahoo.") ||
      host.includes("duckduckgo.com")
    ) {
      return {
        sourceType: "organic",
        sourceName: host,
        utmSource: "",
        utmMedium: "",
        utmCampaign: "",
        referrer: document.referrer,
        referrerDomain: host,
      };
    }

    return {
      sourceType: "referral",
      sourceName: host,
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
      referrer: document.referrer,
      referrerDomain: host,
    };
  } catch {
    return {
      sourceType: "referral",
      sourceName: "Referral",
      utmSource: "",
      utmMedium: "",
      utmCampaign: "",
      referrer: document.referrer || "",
      referrerDomain: "",
    };
  }
};

const hasAnalyticsConsent = () => {
  if (!ANALYTICS_CONFIG.requireConsent) return true;

  try {
    const saved = JSON.parse(localStorage.getItem("gofoton_cookie_consent"));
    return saved?.analytics === true;
  } catch {
    return false;
  }
};

export const analyticsAllowed = (path = window.location.pathname) => {
  if (!ANALYTICS_CONFIG.enabled) {
    return false;
  }

  if (isExcludedPath(path)) {
    return false;
  }

  try {
    /*
      Prevent the heatmap preview iframe
      from generating fake analytics.
    */
    if (new URLSearchParams(window.location.search).get("analyticsPreview") === "1") {
      return false;
    }
  } catch {
    // Continue when URL parsing is unavailable.
  }

  return hasAnalyticsConsent();
};

const getVisitorId = () => {
  let visitorId = localStorage.getItem(VISITOR_KEY);

  if (!visitorId) {
    visitorId = makeId("visitor");
    localStorage.setItem(VISITOR_KEY, visitorId);
  }

  return visitorId;
};

const getSessionState = () => {
  const now = Date.now();

  try {
    const saved = JSON.parse(localStorage.getItem(SESSION_KEY));

    if (saved?.sessionId && now - Number(saved.lastActivityAt || 0) < ANALYTICS_CONFIG.sessionTimeoutMs) {
      const next = { ...saved, lastActivityAt: now };
      localStorage.setItem(SESSION_KEY, JSON.stringify(next));
      return { ...next, createdNewSession: false };
    }
  } catch {
    localStorage.removeItem(SESSION_KEY);
  }

  const next = {
    sessionId: makeId("session"),
    lastActivityAt: now,
  };

  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  return { ...next, createdNewSession: true };
};

const touchSessionStorage = (sessionId) => {
  localStorage.setItem(
    SESSION_KEY,
    JSON.stringify({
      sessionId,
      lastActivityAt: Date.now(),
    })
  );
};

const cleanData = (data = {}) => Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined));

export const getAnalyticsContext = async () => {
  if (!analyticsAllowed()) {
    return null;
  }

  const visitorId = getVisitorId();

  if (await isBrowserExcluded(visitorId)) {
    return null;
  }

  /*
      This endpoint now supplies
      geography only. It no longer
      determines exclusions.
    */
  const analyticsIdentity = await getServerAnalyticsIdentity();

  const sessionState = getSessionState();

  if (cachedContext?.sessionId === sessionState.sessionId) {
    touchSessionStorage(sessionState.sessionId);
    return cachedContext;
  }

  if (initializingContext) return initializingContext;

  initializingContext = (async () => {
    const now = Date.now();
    const sessionId = sessionState.sessionId;

    const visitorRef = doc(db, "analyticsVisitors", visitorId);
    const sessionRef = doc(db, "analyticsSessions", sessionId);

    const [visitorSnap, sessionSnap] = await Promise.all([getDoc(visitorRef), getDoc(sessionRef)]);

    const visitorData = visitorSnap.exists() ? visitorSnap.data() : {};
    const sessionData = sessionSnap.exists() ? sessionSnap.data() : {};

    const isNewVisitor = !visitorSnap.exists();
    const previousVisitClientTimestamp = visitorData.lastSeenClientTimestamp || null;
    const traffic = sessionSnap.exists()
      ? {
          sourceType: sessionData.sourceType || "direct",
          sourceName: sessionData.sourceName || "Direct",
          utmSource: sessionData.utmSource || "",
          utmMedium: sessionData.utmMedium || "",
          utmCampaign: sessionData.utmCampaign || "",
          referrer: sessionData.referrer || "",
          referrerDomain: sessionData.referrerDomain || "",
        }
      : getTrafficSource();

    const deviceCategory = sessionData.deviceCategory || getDeviceCategory();
    const countryCode = sessionData.countryCode || analyticsIdentity?.countryCode || "";
    const regionCode = sessionData.regionCode || analyticsIdentity?.regionCode || "";
    const city = sessionData.city || analyticsIdentity?.city || "";
    const region = sessionData.region || analyticsIdentity?.region || "";

    const visitorPayload = {
      visitorId,
      lastSeenAt: serverTimestamp(),
      lastSeenClientTimestamp: now,
    };

    if (!visitorSnap.exists()) {
      visitorPayload.firstSeenAt = serverTimestamp();
      visitorPayload.firstSeenClientTimestamp = now;
    }

    await setDoc(visitorRef, visitorPayload, { merge: true });

    if (!sessionSnap.exists()) {
      await setDoc(sessionRef, {
        sessionId,
        visitorId,
        date: getDateKey(),
        startedAt: serverTimestamp(),
        startedClientTimestamp: now,
        lastActivityAt: serverTimestamp(),
        lastActivityClientTimestamp: now,
        landingPage: `${window.location.pathname}${window.location.search}`,
        lastPage: `${window.location.pathname}${window.location.search}`,
        pageViewCount: 0,
        isNewVisitor,
        previousVisitClientTimestamp,
        timeSincePreviousVisitMs: previousVisitClientTimestamp ? now - previousVisitClientTimestamp : null,
        deviceCategory,
        countryCode,
        regionCode,
        region,
        city,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        language: navigator.language || "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        screenWidth: window.screen?.width || null,
        screenHeight: window.screen?.height || null,
        ...traffic,
      });
    }

    cachedContext = {
      visitorId,
      sessionId,
      isNewVisitor: sessionData.isNewVisitor ?? isNewVisitor,
      deviceCategory,
      countryCode,
      regionCode,
      region,
      city,
      ...traffic,
    };

    touchSessionStorage(sessionId);
    return cachedContext;
  })();

  try {
    return await initializingContext;
  } finally {
    initializingContext = null;
  }
};

export const trackAnalyticsEvent = async (type, data = {}) => {
  if (!analyticsAllowed()) return;

  try {
    const context = await getAnalyticsContext();
    if (!context) return;

    touchSessionStorage(context.sessionId);

    await addDoc(collection(db, "analyticsEvents"), {
      ...cleanData(data),
      type,
      visitorId: context.visitorId,
      sessionId: context.sessionId,
      date: getDateKey(),
      createdAt: serverTimestamp(),
      clientTimestamp: Date.now(),
      pagePath: data.pagePath || window.location.pathname,
      pageUrl: window.location.href,
      pageTitle: document.title || "",
      deviceCategory: context.deviceCategory,
      sourceType: context.sourceType,
      sourceName: context.sourceName,
    });
  } catch (error) {
    console.error("Analytics event failed:", error);
  }
};

export const trackPageView = async (path, search = "") => {
  if (!analyticsAllowed(path)) return;

  const now = Date.now();
  const pagePath = `${path}${search}`;
  const duplicateKey = `${pagePath}`;

  if (duplicateKey === lastPageViewKey && now - lastPageViewAt < 1500) return;

  lastPageViewKey = duplicateKey;
  lastPageViewAt = now;

  try {
    const context = await getAnalyticsContext();
    if (!context) return;

    await Promise.all([
      addDoc(collection(db, "analyticsEvents"), {
        type: "page_view",
        visitorId: context.visitorId,
        sessionId: context.sessionId,
        date: getDateKey(),
        createdAt: serverTimestamp(),
        clientTimestamp: now,
        pagePath,
        pageUrl: window.location.href,
        pageTitle: document.title || "",
        deviceCategory: context.deviceCategory,
        sourceType: context.sourceType,
        sourceName: context.sourceName,
      }),

      setDoc(
        doc(db, "analyticsSessions", context.sessionId),
        {
          lastActivityAt: serverTimestamp(),
          lastActivityClientTimestamp: now,
          lastPage: pagePath,
          pageViewCount: increment(1),
        },
        { merge: true }
      ),

      setDoc(
        doc(db, "analyticsVisitors", context.visitorId),
        {
          lastSeenAt: serverTimestamp(),
          lastSeenClientTimestamp: now,
        },
        { merge: true }
      ),
    ]);

    touchSessionStorage(context.sessionId);
  } catch (error) {
    console.error("Analytics page view failed:", error);
  }
};

const makeFormDraftId = (sessionId, formName) =>
  `${sessionId}_${String(formName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")}`;

export const saveAnalyticsFormDraft = async (formName, values, options = {}) => {
  if (!analyticsAllowed()) return;

  try {
    const context = await getAnalyticsContext();
    if (!context) return;

    const draftId = makeFormDraftId(context.sessionId, formName);

    await setDoc(
      doc(db, "analyticsFormDrafts", draftId),
      {
        formName,
        visitorId: context.visitorId,
        sessionId: context.sessionId,
        date: getDateKey(),
        status: options.status || "in_progress",
        lastField: options.lastField || "",
        values: cleanData(values),
        pagePath: window.location.pathname,
        updatedAt: serverTimestamp(),
        updatedClientTimestamp: Date.now(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Analytics form draft failed:", error);
  }
};

export const markAnalyticsFormSubmitted = async (formName, values) => {
  await saveAnalyticsFormDraft(formName, values, { status: "submitted" });

  await trackAnalyticsEvent("form_submission", {
    formName,
    category: values.category || "",
    type: values.type || "",
    country: values.country || "",
    state: values.state || "",
  });
};

export const markAnalyticsFormAbandoned = async (formName, values, lastField = "") => {
  await saveAnalyticsFormDraft(formName, values, {
    status: "abandoned",
    lastField,
  });

  await trackAnalyticsEvent("form_abandonment", {
    formName,
    lastField,
    category: values.category || "",
    type: values.type || "",
    country: values.country || "",
    state: values.state || "",
  });
};

export const sendAnalyticsHeartbeat = async () => {
  if (!analyticsAllowed()) return;

  if (document.visibilityState !== "visible") {
    return;
  }

  try {
    const context = await getAnalyticsContext();

    if (!context) return;

    const now = Date.now();

    const pagePath = `${window.location.pathname}${window.location.search}`;

    await setDoc(
      doc(db, "analyticsSessions", context.sessionId),
      {
        lastActivityAt: serverTimestamp(),

        lastActivityClientTimestamp: now,

        liveUpdatedAt: serverTimestamp(),

        liveUpdatedClientTimestamp: now,

        liveVisible: true,

        lastPage: pagePath,

        countryCode: context.countryCode || "",

        regionCode: context.regionCode || "",

        region: context.region || "",

        city: context.city || "",
      },
      {
        merge: true,
      }
    );

    touchSessionStorage(context.sessionId);
  } catch (error) {
    console.error("Analytics heartbeat failed:", error);
  }
};

export const markAnalyticsSessionHidden = async () => {
  if (!analyticsAllowed()) return;

  try {
    const context = await getAnalyticsContext();

    if (!context) return;

    await setDoc(
      doc(db, "analyticsSessions", context.sessionId),
      {
        liveVisible: false,

        liveUpdatedAt: serverTimestamp(),

        liveUpdatedClientTimestamp: Date.now(),
      },
      {
        merge: true,
      }
    );
  } catch (error) {
    console.error("Analytics hidden-state update failed:", error);
  }
};

export const trackAnalyticsTabOpen = (tabName, extra = {}) => {
  return trackAnalyticsEvent("tab_open", {
    tabName: String(tabName || "").trim(),
    ...extra,
  });
};

export const excludeThisBrowserFromAnalytics = () => {
  localStorage.setItem(EXCLUDED_KEY, "true");

  localStorage.setItem(BROWSER_EXCLUSION_MIGRATION_KEY, "true");

  cachedContext = null;
  cachedBrowserExclusion = null;

  browserExclusionPromise = null;

  window.dispatchEvent(new Event("gofoton:analytics-exclusion-changed"));
};

export const includeThisBrowserInAnalytics = () => {
  localStorage.removeItem(EXCLUDED_KEY);

  localStorage.setItem(BROWSER_EXCLUSION_MIGRATION_KEY, "true");

  cachedContext = null;
  cachedBrowserExclusion = null;

  browserExclusionPromise = null;

  window.dispatchEvent(new Event("gofoton:analytics-exclusion-changed"));
};
